// Centralised inline-style tokens for the floating chat widget. The
// goal here is a slightly more refined, "modern messenger" feel:
// tighter geometry, softer shadows, brand-green accent on the active
// row instead of the previous Slack-blue, and a faint gradient on the
// sidebar so it harmonises with the floating button.

export const fixedChatButtonStyle = {
  position: "fixed",
  bottom: "1.5rem",
  right: "1.5rem",
  zIndex: 9999,
  height: "3.5rem",
  width: "3.5rem",
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "9999px",
  // Layered shadow gives a "lifted" look over light backgrounds without
  // the harsh single-drop the previous boxShadow had.
  boxShadow:
    "0 12px 24px -10px rgba(0, 101, 70, 0.4), 0 4px 8px -2px rgba(0, 0, 0, 0.12)",
  color: "#ffffff",
  background:
    "linear-gradient(140deg, #008056 0%, #006546 55%, #004d35 100%)",
  border: "none",
  fontSize: "16px",
  cursor: "pointer",
  fontWeight: 600,
  transition: "transform 0.15s ease, box-shadow 0.15s ease",
};

export const unreadBadgeStyle = {
  display: "inline-flex",
  height: "1.125rem",
  minWidth: "1.125rem",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "9999px",
  backgroundColor: "#dc2626",
  fontSize: "0.6875rem",
  fontWeight: 700,
  color: "#fff",
  padding: "0 0.375rem",
  lineHeight: 1,
  letterSpacing: "0.02em",
};

export const chatContainerStyle = {
  position: "fixed",
  bottom: "5.5rem",
  right: "1.5rem",
  zIndex: 9999,
  transition: "transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s",
};

export const cardStyle = {
  display: "flex",
  flexDirection: "row",
  // 30% bigger panel for more breathing room (was 50 × 34 rem). Capped
  // to the viewport via min() so smaller screens / docked browser
  // windows still get a panel that fits without horizontal scroll.
  height: "min(44rem, calc(100vh - 7rem))",
  width: "min(65rem, calc(100vw - 2.5rem))",
  borderRadius: "1rem",
  backgroundColor: "#ffffff",
  // Bigger soft shadow + a thin inner border instead of a single hard
  // 1px line — reads more like a floating panel than a flat card.
  boxShadow:
    "0 32px 48px -16px rgba(15, 23, 42, 0.25), 0 12px 24px -10px rgba(15, 23, 42, 0.15)",
  border: "1px solid rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
};

export const sidebarStyle = {
  // Sidebar bumped proportionally so the row geometry stays consistent
  // with the larger panel — avatars + name + preview still breathe.
  width: "22rem",
  background: "linear-gradient(180deg, #1c1f24 0%, #181b1f 100%)",
  display: "flex",
  flexDirection: "column",
  borderRight: "1px solid rgba(255, 255, 255, 0.06)",
};

export const sidebarHeaderStyle = {
  padding: "0.875rem 1rem",
  borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "rgba(255, 255, 255, 0.02)",
};

export const sidebarSectionStyle = {
  padding: "0.5rem 0 0.75rem",
  borderBottom: "1px solid rgba(255, 255, 255, 0.04)",
};

export const sidebarSectionTitleStyle = {
  padding: "0.625rem 1rem 0.5rem",
  fontSize: "0.6875rem",
  fontWeight: 700,
  color: "#9ca3af",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const sidebarItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "0.625rem",
  padding: "0.45rem 0.75rem",
  margin: "1px 0.5rem",
  borderRadius: "6px",
  color: "#d1d5db",
  fontSize: "0.9rem",
  cursor: "pointer",
  border: "none",
  backgroundColor: "transparent",
  width: "calc(100% - 1rem)",
  textAlign: "left",
  transition: "background-color 0.12s ease, color 0.12s ease",
};

export const sidebarItemActiveStyle = {
  ...sidebarItemStyle,
  // Brand-green accent instead of the previous Slack-blue. Reads as
  // "this is the kinesin product" rather than a generic chat clone.
  backgroundColor: "rgba(34, 197, 94, 0.18)",
  color: "#ffffff",
  boxShadow: "inset 2px 0 0 #22c55e",
};

export const mainContentStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#fafafa",
};

export const cardHeaderStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid #e5e7eb",
  padding: "0.875rem 1.125rem",
  backgroundColor: "#ffffff",
};

export const cardTitleStyle = {
  marginBottom: 0,
  display: "flex",
  alignItems: "center",
  fontSize: "1rem",
  fontWeight: 600,
  color: "#111827",
  letterSpacing: "-0.005em",
};

export const cardContentStyle = {
  flex: 1,
  padding: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};
